import { Injectable, signal } from '@angular/core'
import { io, Socket } from 'socket.io-client'
import { environment } from '../../../environments/environment'

export type PM2Action = 'start' | 'stop' | 'restart' | 'reload' | 'delete'
export type PM2LogLevel = 'out' | 'err'

export interface PM2Process {
  name: string
  pm_id: number
  pid: number | null
  status: string
  cpu: number
  memory: number
  uptime: number
  restarts: number
  unstable_restarts: number
  version: string | null
  exec_mode: string | null
  instances: number
  script: string | null
  out_log: string | null
  err_log: string | null
}

export interface PM2LogEntry {
  name?: string
  level: PM2LogLevel
  data: string
  timestamp: number
  raw?: string
  message?: string
  severity?: string
  rule?: string
}

export interface PM2ActionResult {
  ok: boolean
  name?: string
  action?: PM2Action
  error?: string
}

export interface PM2DiskUsage {
  filesystem: string
  size: number
  used: number
  available: number
  usePercent: string
  mountedOn: string
  iSize?: number
  iUsed?: number
  iAvailable?: number
  iUsePercent?: string
}

export interface PM2FolderSize {
  base: string
  path: string
  size: number
}

export interface PM2ServiceStatus {
  name: string
  loadState: string
  activeState: string
  subState: string
  unitFileState: string
  mainPid: string
  since: string
  status: string
}

export interface PM2SystemInfo {
  supported: boolean
  loading: boolean
  error: string | null
  updatedAt: number
  disk: PM2DiskUsage[]
  folders: PM2FolderSize[]
  services: PM2ServiceStatus[]
  resources: PM2Resources | null
  processes: PM2Processes
  network: PM2NetInterface[]
  ports: PM2Port[]
  diskio: PM2DiskIo[]
}

export interface PM2Resources {
  mem: {
    total: number
    free: number
    available: number
    used: number
    buffers: number
    cached: number
    usedPercent: number
  }
  swap: {
    total: number
    free: number
    used: number
    usedPercent: number
  }
  cpu: {
    usagePercent: number
    cores: number
    model: string
  }
  load: {
    one: number
    five: number
    fifteen: number
  }
  uptime: number
}

export interface PM2ProcessStat {
  pid: number
  user: string
  cpu: number
  mem: number
  rss: number
  command: string
}

export interface PM2Processes {
  byCpu: PM2ProcessStat[]
  byMem: PM2ProcessStat[]
}

export interface PM2NetInterface {
  name: string
  rxBytes: number
  rxPackets: number
  rxErrors: number
  rxDropped: number
  txBytes: number
  txPackets: number
  txErrors: number
  txDropped: number
  rxRate: number
  txRate: number
}

export interface PM2Port {
  address: string
  process: string
  pid: number | null
}

export interface PM2DiskIo {
  name: string
  readBytes: number
  writeBytes: number
  readRate: number
  writeRate: number
}

export interface PM2ProcessDetail {
  name: string
  pm_id: number
  pid: number | null
  status: string
  version: string | null
  execMode: string | null
  instances: number
  nodeVersion: string | null
  script: string | null
  cwd: string | null
  interpreter: string | null
  args: string[] | string | null
  autorestart: boolean
  watch: boolean
  maxMemoryRestart: string | number | null
  killTimeout: number | null
  listenTimeout: number | null
  restartTime: number
  unstableRestarts: number
  createdAt: number | null
  uptime: number
  exitCode: number | null
  outLog: string | null
  errLog: string | null
  env: Record<string, unknown>
  envMasked: boolean
}

export interface PM2MetricPoint {
  ts: number
  cpu: number
  load1: number
  load5: number
  load15: number
  memUsed: number
  memTotal: number
  swapUsed: number
  swapTotal: number
  procCount: number
  onlineCount: number
  erroredCount: number
}

export interface PM2Event {
  ts: number
  name: string
  type: string
  detail: string
}

export interface PM2Alert {
  ts: number
  level: string
  title: string
  message: string
  source: string
}

export interface PM2HistoryInfo {
  enabled: boolean
  driver: string
  dbFile: string | null
  retentionHours: number
  flaggedRetentionHours: number
  metricsInterval: number
  persistLogs: boolean
}

export interface PM2LogRule {
  id: number
  name: string
  pattern: string
  is_regex: number
  process: string | null
  level: string | null
  severity: string
  active: number
  notify: number
  created_at: number
  hit_count: number
}

export interface PM2FlaggedLog {
  id: number
  ts: number
  name: string
  level: string
  message: string
  rule_id: number | null
  rule_name: string | null
  severity: string
  pinned: number
  note: string
  created_at: number
}

export interface PM2LogSearchOptions {
  name?: string
  query?: string
  level?: string
  hours?: number
  from?: number
  to?: number
  limit?: number
}

export interface PM2LogAnalytics {
  window: number
  perProcess: { name: string, total: number, errors: number }[]
  perHour: { hour: number, total: number, errors: number }[]
  topErrors: { name: string, message: string, count: number }[]
}

export interface PM2Settings {
  retentionHours: number
  flaggedRetentionHours: number
}

const EMPTY_SYSTEM_INFO: PM2SystemInfo = {
  supported: true,
  loading: false,
  error: null,
  updatedAt: 0,
  disk: [],
  folders: [],
  services: [],
  resources: null,
  processes: { byCpu: [], byMem: [] },
  network: [],
  ports: [],
  diskio: []
}

const MAX_LOG_ENTRIES = 2000
const MAX_METRIC_POINTS = 600

@Injectable({
  providedIn: 'root'
})
export class PM2MonitorService {
  private socket: Socket | null = null

  readonly connected = signal(false)
  readonly pm2Connected = signal(false)
  readonly allowActions = signal(true)
  readonly processes = signal<PM2Process[]>([])
  readonly logs = signal<PM2LogEntry[]>([])
  readonly selected = signal<string>('*')
  readonly lastUpdate = signal<number | null>(null)
  readonly system = signal<PM2SystemInfo>(EMPTY_SYSTEM_INFO)
  readonly systemLoading = signal(false)
  readonly history = signal<PM2HistoryInfo | null>(null)
  readonly detail = signal<PM2ProcessDetail | null>(null)
  readonly metricsHistory = signal<PM2MetricPoint[]>([])
  readonly events = signal<PM2Event[]>([])
  readonly alerts = signal<PM2Alert[]>([])
  readonly rules = signal<PM2LogRule[]>([])
  readonly flagged = signal<PM2FlaggedLog[]>([])
  readonly analytics = signal<PM2LogAnalytics | null>(null)
  readonly settings = signal<PM2Settings | null>(null)
  readonly historyResults = signal<PM2LogEntry[]>([])
  readonly historyLoading = signal(false)

  connect (): void {
    if (this.socket) {
      return
    }
    this.socket = io(environment.PM2_MONITOR_URL, {
      path: environment.PM2_MONITOR_PATH,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 2000
    })

    this.socket.on('connect', () => this.connected.set(true))
    this.socket.on('disconnect', () => this.connected.set(false))
    this.socket.on('connect_error', () => {
      this.connected.set(false)
      this.pm2Connected.set(false)
    })

    this.socket.on('pm2:info', (info: { connected: boolean, allowActions: boolean, history?: PM2HistoryInfo }) => {
      this.pm2Connected.set(!!info?.connected)
      this.allowActions.set(info?.allowActions !== false)
      this.history.set(info?.history || null)
    })

    this.socket.on('pm2:processes', (list: PM2Process[]) => {
      this.processes.set(list || [])
      this.lastUpdate.set(Date.now())
    })

    this.socket.on('pm2:logs:history', (payload: { name: string, entries: PM2LogEntry[] }) => {
      if (this.selected() === payload.name) {
        this.logs.set((payload.entries || []).map(entry => ({
          name: payload.name,
          level: entry.level || 'out',
          timestamp: entry.timestamp || 0,
          data: entry.data ?? entry.message ?? entry.raw ?? ''
        })))
      }
    })

    this.socket.on('pm2:log', (entry: PM2LogEntry) => {
      const selected = this.selected()
      if (selected === '*' || selected === entry.name) {
        this.logs.update(list => {
          const next = list.length >= MAX_LOG_ENTRIES ? list.slice(list.length - MAX_LOG_ENTRIES + 1) : list.slice()
          next.push(entry)
          return next
        })
      }
    })

    this.socket.on('system:data', (info: PM2SystemInfo) => {
      this.system.set(info || EMPTY_SYSTEM_INFO)
      this.systemLoading.set(!!info?.loading)
    })

    this.socket.on('system:loading', (loading: boolean) => {
      this.systemLoading.set(!!loading)
    })

    this.socket.on('metrics:history', (payload: { points: PM2MetricPoint[] }) => {
      this.metricsHistory.set(payload?.points || [])
    })

    this.socket.on('metrics:sample', (sample: PM2MetricPoint) => {
      if (!sample) {
        return
      }
      this.metricsHistory.update(list => {
        const next = list.concat(sample)
        return next.length > MAX_METRIC_POINTS ? next.slice(next.length - MAX_METRIC_POINTS) : next
      })
    })

    this.socket.on('events:data', (events: PM2Event[]) => this.events.set(events || []))
    this.socket.on('alerts:data', (alerts: PM2Alert[]) => this.alerts.set(alerts || []))
    this.socket.on('rules:data', (rules: PM2LogRule[]) => this.rules.set(rules || []))
    this.socket.on('flagged:data', (flagged: PM2FlaggedLog[]) => this.flagged.set(flagged || []))
    this.socket.on('analytics:data', (analytics: PM2LogAnalytics) => {
      this.analytics.set(analytics || null)
      this.historyLoading.set(false)
    })
    this.socket.on('settings:data', (settings: PM2Settings) => this.settings.set(settings || null))
  }

  disconnect (): void {
    if (!this.socket) {
      return
    }
    this.socket.removeAllListeners()
    this.socket.disconnect()
    this.socket = null
    this.connected.set(false)
    this.pm2Connected.set(false)
  }

  refresh (): void {
    this.socket?.emit('pm2:refresh')
  }

  refreshSystem (): void {
    this.systemLoading.set(true)
    this.socket?.emit('system:refresh')
  }

  select (name: string): void {
    const previous = this.selected()
    if (previous === name) {
      return
    }
    if (this.socket && previous) {
      this.socket.emit('pm2:logs:unsubscribe', { name: previous })
    }
    this.selected.set(name)
    this.logs.set([])
    this.socket?.emit('pm2:logs:subscribe', { name, lines: 300 })
  }

  clearLogs (): void {
    this.logs.set([])
  }

  action (name: string, action: PM2Action): Promise<PM2ActionResult> {
    return new Promise((resolve) => {
      if (!this.socket) {
        return resolve({ ok: false, error: 'Not connected to the PM2 monitoring service' })
      }
      let settled = false
      const timeout = setTimeout(() => {
        if (!settled) {
          settled = true
          resolve({ ok: false, error: 'Timeout waiting for the PM2 monitoring service' })
        }
      }, 15000)
      this.socket.emit('pm2:action', { name, action }, (result: PM2ActionResult) => {
        if (settled) {
          return
        }
        settled = true
        clearTimeout(timeout)
        resolve(result || { ok: false, error: 'Empty response' })
      })
    })
  }

  describe (name: string): Promise<PM2ProcessDetail | null> {
    return this.emitAck<{ ok: boolean, detail?: PM2ProcessDetail }>('pm2:describe', { name })
      .then(result => result?.detail ?? null)
  }

  flushLogs (name = 'all'): Promise<PM2ActionResult> {
    return this.emitAck<PM2ActionResult>('pm2:flush', { name })
      .then(result => result ?? { ok: false, error: 'Empty response' })
  }

  loadMetricsHistory (hours?: number): void {
    this.socket?.emit('metrics:history', { hours })
  }

  loadEvents (limit = 200): void {
    this.socket?.emit('events:list', { limit })
  }

  loadAlerts (limit = 100): void {
    this.socket?.emit('alerts:list', { limit })
  }

  searchLogs (options: PM2LogSearchOptions): Promise<PM2LogEntry[]> {
    this.historyLoading.set(true)
    return this.emitAck<{ ok: boolean, entries: { ts: number, name: string, level: PM2LogLevel, message: string }[] }>(
      'logs:search', options
    ).then(result => {
      const entries = (result?.entries || []).map(entry => ({
        name: entry.name,
        level: entry.level,
        timestamp: entry.ts,
        data: entry.message
      }))
      this.historyResults.set(entries)
      this.historyLoading.set(false)
      return entries
    })
  }

  loadRules (): void {
    this.socket?.emit('rules:list')
  }

  saveRule (rule: Partial<PM2LogRule>): Promise<PM2ActionResult> {
    return this.emitAck<PM2ActionResult>('rules:save', rule).then(result => result ?? { ok: false })
  }

  deleteRule (id: number): Promise<PM2ActionResult> {
    return this.emitAck<PM2ActionResult>('rules:delete', { id }).then(result => result ?? { ok: false })
  }

  loadFlagged (options?: { name?: string, severity?: string, pinned?: boolean, limit?: number }): void {
    this.socket?.emit('flagged:list', options || { limit: 200 })
  }

  pinFlagged (id: number, pinned: boolean): Promise<PM2ActionResult> {
    return this.emitAck<PM2ActionResult>('flagged:pin', { id, pinned }).then(result => result ?? { ok: false })
  }

  deleteFlagged (id: number): Promise<PM2ActionResult> {
    return this.emitAck<PM2ActionResult>('flagged:delete', { id }).then(result => result ?? { ok: false })
  }

  clearFlagged (onlyUnpinned: boolean): Promise<PM2ActionResult> {
    return this.emitAck<PM2ActionResult>('flagged:clear', { onlyUnpinned }).then(result => result ?? { ok: false })
  }

  loadAnalytics (hours?: number): void {
    this.historyLoading.set(true)
    this.socket?.emit('analytics:logs', { hours })
  }

  loadSettings (): void {
    this.socket?.emit('settings:get')
  }

  saveSettings (patch: Partial<PM2Settings>): Promise<PM2ActionResult> {
    return this.emitAck<PM2ActionResult>('settings:set', patch).then(result => result ?? { ok: false })
  }

  private emitAck<T> (event: string, payload: unknown, timeoutMs = 20000): Promise<T | null> {
    return new Promise((resolve) => {
      if (!this.socket) {
        return resolve(null)
      }
      let settled = false
      const timeout = setTimeout(() => {
        if (!settled) {
          settled = true
          resolve(null)
        }
      }, timeoutMs)
      this.socket.emit(event, payload, (result: T) => {
        if (settled) {
          return
        }
        settled = true
        clearTimeout(timeout)
        resolve(result ?? null)
      })
    })
  }
}
